import { useState, useEffect, useRef, useMemo } from 'react';
import { Badge, Button, Modal, List, Typography, Space, Empty, App } from 'antd';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { colors } from '@/lib/antd-config';
import { useQuery } from '@apollo/client/react';
import { MESSAGES_QUERY, MessagesResponse } from '@/graphql/queries/messages';
import { apolloClientV2 } from '@/lib/apollo-client-v2';
import { usePermissions } from '@/hooks/usePermissions';
import './Notifications.css';

const { Text, Paragraph } = Typography;

interface NotificationsProps {
  onLogin?: boolean; // If true, show urgent messages in modal on mount
}

export function Notifications({ onLogin = false }: NotificationsProps) {
  // App.useApp() context is required for Ant Design components
  App.useApp();
  const { hasPermission } = usePermissions();
  const canNotify = hasPermission('system.messages.notify');
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [dismissedMessageIds, setDismissedMessageIds] = useState<Set<number>>(new Set());
  const [hasHandledLoginUrgent, setHasHandledLoginUrgent] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);

  // Poll for active messages every 10 seconds - only if user has notify permission
  const { data, loading, error, previousData } = useQuery<MessagesResponse>(MESSAGES_QUERY, {
    variables: { active: true },
    pollInterval: 10000,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: !canNotify,
    client: apolloClientV2,
  });

  // Use current data if available, otherwise fall back to previous data to prevent flicker
  const currentData = data || previousData;
  // Ensure messages is always an array, even if the response structure is unexpected
  // Handle both direct array and nested structure
  let messages: any[] = [];
  if (currentData) {
    if (Array.isArray(currentData.messages)) {
      messages = currentData.messages;
    } else if (currentData.messages && typeof currentData.messages === 'object' && 'edges' in currentData.messages) {
      // Handle paginated response structure
      const messagesWithEdges = currentData.messages as { edges: any[] };
      if (Array.isArray(messagesWithEdges.edges)) {
        messages = messagesWithEdges.edges.map((edge: any) => edge.node || edge);
      }
    } else if (currentData && typeof currentData === 'object' && 'data' in currentData) {
      // Handle nested data structure
      const dataWithNested = currentData as { data: { messages?: any[] } };
      if (dataWithNested.data && Array.isArray(dataWithNested.data.messages)) {
        messages = dataWithNested.data.messages;
      }
    }
  }
  
  // Memoize filtered messages to prevent unnecessary recalculations
  const visibleMessages = useMemo(() => {
    return messages.filter(msg => msg && !dismissedMessageIds.has(msg.id));
  }, [messages, dismissedMessageIds]);
  
  const urgentMessages = useMemo(() => {
    return visibleMessages.filter(msg => msg && msg.urgency === 'URGENT');
  }, [visibleMessages]);
  
  const unreadCount = visibleMessages.length;
  
  // Only show loading if we have no data at all (initial load)
  const isInitialLoading = loading && !currentData;

  if (onLogin && urgentMessages.length > 0 && !hasHandledLoginUrgent) {
    setHasHandledLoginUrgent(true);
    setIsModalVisible(true);
  }
  
  // Debug logging (remove in production if needed)
  useEffect(() => {
    if (isDropdownVisible || isModalVisible) {
      console.log('Notifications Debug:', {
        hasData: !!currentData,
        currentData,
        messagesCount: messages.length,
        messages,
        visibleMessagesCount: visibleMessages.length,
        visibleMessages,
        dismissedMessageIds: Array.from(dismissedMessageIds),
        error: error?.message,
        loading,
        canNotify,
      });
    }
  }, [isDropdownVisible, isModalVisible, currentData, messages, visibleMessages, dismissedMessageIds, error, loading, canNotify]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownVisible &&
        dropdownRef.current &&
        bellButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !bellButtonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    };

    if (isDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownVisible]);

  const handleDismissMessage = (messageId: number) => {
    setDismissedMessageIds(prev => new Set([...prev, messageId]));
    
    // If this was the last message, close the modal/dropdown
    if (visibleMessages.length === 1) {
      setIsModalVisible(false);
      setIsDropdownVisible(false);
    }
  };

  const handleDismissAll = () => {
    visibleMessages.forEach(msg => {
      setDismissedMessageIds(prev => new Set([...prev, msg.id]));
    });
    setIsModalVisible(false);
    setIsDropdownVisible(false);
  };

  const handleBellClick = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  // Always show the icon, even if there's an error
  // The icon should always be visible - badge only shows when count > 0
  if (error) {
    // Don't show error to user for background polling, but still render the icon
  }

  // Don't render anything if user doesn't have notify permission
  if (!canNotify) {
    return null;
  }

  return (
    <>
      <Badge 
        count={unreadCount > 0 ? unreadCount : undefined} 
        size="small" 
        offset={[-2, 2]}
      >
        <Button
          ref={bellButtonRef}
          type="text"
          icon={<BellOutlined style={{ fontSize: '18px' }} />}
          onClick={handleBellClick}
          style={{
            padding: '4px 8px',
            height: 'auto',
            minWidth: '44px',
            minHeight: '44px',
            color: unreadCount > 0 ? colors.info : colors.textSecondary,
          }}
          title={unreadCount > 0 ? `${unreadCount} notification${unreadCount !== 1 ? 's' : ''}` : 'No notifications'}
        />
      </Badge>

      {/* Dropdown for viewing all messages */}
      {isDropdownVisible && (
        <div
          ref={dropdownRef}
          className="notifications-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            width: 'min(400px, calc(100vw - 16px))',
            maxHeight: '500px',
            backgroundColor: colors.bgContainer,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            color: colors.textPrimary, // Ensure default text color is dark
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${colors.borderLight}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: colors.bgElevated,
            }}
          >
            <Text strong style={{ color: colors.textPrimary, fontSize: '16px', fontWeight: 600 }}>Notifications</Text>
            <Space>
              {unreadCount > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={handleDismissAll}
                  style={{ padding: 0, color: colors.info }}
                >
                  Dismiss All
                </Button>
              )}
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setIsDropdownVisible(false)}
                style={{ padding: 0, color: colors.textPrimary }}
              />
            </Space>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', minHeight: '100px' }}>
            {isInitialLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Text type="secondary" style={{ color: colors.textPrimary }}>Loading...</Text>
              </div>
            ) : error && !currentData ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Text type="danger" style={{ color: colors.error }}>Error loading notifications</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px', color: colors.textSecondary }}>
                  {error.message}
                </Text>
              </div>
            ) : visibleMessages.length === 0 ? (
              <Empty
                description={<span style={{ color: colors.textSecondary }}>No notifications</span>}
                style={{ padding: '40px 0' }}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={visibleMessages}
                locale={{ emptyText: 'No notifications' }}
                style={{ padding: 0, color: colors.textPrimary }}
                renderItem={(msg) => {
                  if (!msg || !msg.id) {
                    console.warn('Invalid message item:', msg);
                    return null;
                  }
                  return (
                    <List.Item
                      key={msg.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: `1px solid ${colors.borderLight}`,
                        backgroundColor: msg.urgency === 'URGENT' ? '#fff1f0' : colors.bgContainer,
                        color: colors.textPrimary,
                      }}
                      actions={[
                        <Button
                          key="dismiss"
                          type="text"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleDismissMessage(msg.id)}
                          style={{ padding: 0, color: colors.textSecondary }}
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong style={{
                              color: msg.urgency === 'URGENT' ? colors.error : colors.textPrimary,
                              fontSize: '14px',
                              fontWeight: 600,
                            }}>
                              {msg.urgency === 'URGENT' ? 'Urgent' : 'Normal'}
                            </Text>
                          </Space>
                        }
                        description={
                          <Paragraph
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              color: colors.textPrimary,
                              fontSize: '14px',
                              lineHeight: '1.5',
                            }}
                            ellipsis={{ rows: 3, expandable: true }}
                          >
                            {msg.message || '(No message content)'}
                          </Paragraph>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Modal for urgent messages on login */}
      <Modal
        title={
          <Space>
            <BellOutlined style={{ color: colors.error }} />
            <Text strong>Urgent Notifications</Text>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="dismiss-all" onClick={handleDismissAll}>
            Dismiss All
          </Button>,
          <Button key="close" type="primary" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={600}
        styles={{
          body: {
            maxHeight: '500px',
            overflowY: 'auto',
          },
        }}
      >
        {urgentMessages.length === 0 ? (
          <Empty description="No urgent notifications" />
        ) : (
          <List
            dataSource={urgentMessages}
            renderItem={(msg) => {
              if (!msg) return null;
              return (
                <List.Item
                  key={msg.id}
                  style={{
                    padding: '12px 0',
                    borderBottom: `1px solid ${colors.borderLight}`,
                  }}
                  actions={[
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleDismissMessage(msg.id)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={<Text strong style={{ color: colors.error }}>Urgent</Text>}
                    description={
                      <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {msg.message || '(No message content)'}
                      </Paragraph>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </>
  );
}

